/*
 * Brand primitives. Guidelines v1.0.
 *
 * Flat fields and rules throughout. No gradients, anywhere, ever.
 */

/* --- Progress strip ----------------------------------------------------- */

.strip {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  line-height: 0;
}

.strip i {
  display: block;
  background: var(--gb-rule);
}

/*
 * The bars, in the panel's own colours.
 *
 * `current` is the newest version, the filled download chunk, the top bar of
 * the mark — the one the eye should land on, so it takes the accent. `past` is
 * everything behind it in plain type colour, and `dim` is a neutral tint of the
 * ground for a bar that is not filled at all.
 *
 * These were Volt and Build. Both are wrong now: a panel gets one accent, and
 * naming the channel here meant the mark in the navigation bar was blue on a
 * store whose palette has no blue in it.
 */
.strip i[data-bar='current'] {
  background: var(--gb-accent);
}
.strip i[data-bar='past'] {
  background: var(--gb-text);
}
/*
 * `--gb-rule`, not `--gb-ink-15`.
 *
 * A dim bar is "a neutral tint of this panel's ground", and the ink ramp cannot
 * express that — #d9d9e0 is a near-white, correct on Paper and a bright bar on
 * Ink, which reads as *filled* rather than as empty. The rule token is already
 * that tint on every ground, so the mark is now right on all three spaces
 * without anybody passing a prop.
 */
.strip i[data-bar='dim'] {
  background: var(--gb-rule);
}

/* On Ink, the dim bars need to lift off the ground rather than vanish. */
.strip i[data-on-dark][data-bar='dim'],
.strip[data-on-dark] i[data-bar='dim'] {
  background: #33333f;
}

.stripTiny {
  height: 0.85rem;
  gap: 1.2px;
}
.stripTiny i {
  width: 0.4rem;
}

.stripSm {
  height: 1.25rem;
}
.stripSm i {
  width: 0.55rem;
}

.stripMd {
  height: 2.25rem;
}
.stripMd i {
  width: 1rem;
}

.stripLg {
  height: 5rem;
  gap: 3px;
}
.stripLg i {
  width: 2rem;
}

/* --- Logo lockup -------------------------------------------------------- */

/*
 * The lockup states its own colour rather than inheriting one.
 *
 * Section 02: the wordmark is solid — Ink on light, Paper on dark — and never
 * takes a channel colour. Both lockups are usually an `<a>`, and an anchor with
 * no colour of its own is whatever the user agent decides, which is blue until
 * somebody has visited the home page and purple afterwards. The document base
 * in `tokens.css` fixes that everywhere at once; this is here as well because
 * the mark is the one element that must be right on a surface that imports the
 * components without the base, and because "solid, never a colour" is a rule
 * about the mark rather than a side effect of a reset.
 */
.logo,
.logoRow {
  color: var(--gb-text);
}

.logo {
  display: inline-flex;
  flex-direction: column;
  gap: 0.5rem;
  text-decoration: none;
}

.logoRow {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  text-decoration: none;
}

/*
 * On a dark ground the mark is Paper. `onDark` is for the case the surface
 * tokens cannot answer — a mark sitting on a flood band or an image inside an
 * otherwise light page, where `--gb-text` is still Ink.
 */
.logoOnDark {
  color: var(--gb-paper);
}

.word {
  font-family: var(--gb-font-display);
  font-variation-settings:
    'wdth' 125,
    'wght' 900;
  text-transform: uppercase;
  letter-spacing: -0.025em;
  line-height: 0.8;
  display: block;
}

/* The mark and, on the two work sites, the site name beside it. */
.wordLockup {
  display: flex;
  align-items: baseline;
  gap: 0.4em;
}

/*
 * Which of the three sites this is.
 *
 * Martian Mono and the data-label treatment, so it reads as a label attached to
 * the mark rather than as part of the wordmark — the guidelines are explicit
 * that the wordmark is never restyled or extended.
 */
.wordSite {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: var(--gb-tracking-data);
  color: var(--gb-accent);
  white-space: nowrap;
}

/* --- Runner (running header) -------------------------------------------- */

.runner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--gb-space-6);
  border-bottom: 2px solid var(--gb-text);
  padding-bottom: var(--gb-space-2);
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--gb-text);
}

/* --- Stat --------------------------------------------------------------- */

.stat {
  border: 1px solid var(--gb-text);
  padding: var(--gb-space-4);
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
}

/*
 * The figure takes the panel's accent, always.
 *
 * There used to be a `channel` prop choosing between the four. It was the main
 * way a page ended up with three signal colours in one row of statistics, and
 * with the panel palettes there is nothing left for it to choose between: a
 * developers stat is Build because every developers accent is Build.
 */
.statValue {
  font-family: var(--gb-font-mono);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  font-weight: 700;
  font-size: clamp(1.75rem, 4vw, 2.75rem);
  line-height: 1;
  letter-spacing: -0.04em;
  color: var(--gb-accent);
}

.statLabel {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--gb-text-muted);
}

/* --- Do / Never --------------------------------------------------------- */

.dd {
  border-top: 3px solid var(--gb-text);
  padding-top: var(--gb-space-3);
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-3);
}

/*
 * The pair is drawn from the panel's own two working colours: the accent for
 * the affirmative, plain type colour for the prohibition.
 *
 * It used to be Build against Hot, which put #ff2d6f directly on the #2b3dff
 * flood band on the developers page — two saturated hues at similar value,
 * which vibrate against each other and were the worst-looking thing on the
 * site. Hot is also simply not in the developers palette. Type colour reads as
 * the sterner of the two against an accent, which is the right way round.
 */
.ddYes {
  border-color: var(--gb-accent);
}
.ddNo {
  border-color: var(--gb-text);
}

.ddTitle {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin: 0;
}

.ddYes .ddTitle {
  color: var(--gb-accent);
}
.ddNo .ddTitle {
  color: var(--gb-text);
}

.dd ul {
  margin: 0;
  padding-left: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
}

/* --- Section band ------------------------------------------------------- */

/*
 * A full-bleed colour field. The guidelines' flood pages, translated to a
 * scrolling page: one channel owns the band, and the band owns the surface
 * role tokens inside it so nested components invert correctly.
 */
.band {
  margin: var(--gb-space-16) calc(50% - 50vw) 0;
  padding: var(--gb-space-16) calc(50vw - 50%);
}

.bandInk {
  background: var(--gb-ink);
  --gb-text: var(--gb-paper);
  --gb-text-muted: #a8a8b8;
  --gb-text-faint: #6e6e80;
  --gb-rule: #2a2a38;
  /* The ground a child inverts against is this band, not the page under it. */
  --gb-surface: var(--gb-ink);
  color: var(--gb-paper);
}

/*
 * A flood of the panel's own accent: Solar on the store, Hot on creators, Build
 * on developers. It was always Build, which meant the store's one flood section
 * was blue on a panel whose palette has no blue in it.
 *
 * Everything inside inverts. The type colour becomes whatever that accent takes
 * — Ink on Solar, Paper on Build and Hot, which the `--gb-on-accent-base` token
 * already knows — and `--gb-accent` swaps to it as well, since the flood *is*
 * the accent and anything drawn in it would otherwise vanish into the ground.
 * `--gb-on-accent` swaps the other way, so a filled button inside the band is a
 * block of type colour with the flood colour showing through the letters.
 */
.bandAccent {
  background: var(--gb-accent-base);
  color: var(--gb-on-accent-base);

  --gb-text: var(--gb-on-accent-base);
  --gb-accent: var(--gb-on-accent-base);
  --gb-on-accent: var(--gb-accent-base);
  /* The ground a child inverts against is this band, not the page under it. */
  --gb-surface: var(--gb-accent-base);

  /*
   * Muted type, faint type and rules are washes of the band's own type colour,
   * rather than the fixed translucent whites this used to carry — those were
   * correct on Build and would have been a pale orange haze on Solar.
   */
  --gb-text-muted: color-mix(in srgb, var(--gb-on-accent-base) 82%, transparent);
  --gb-text-faint: color-mix(in srgb, var(--gb-on-accent-base) 62%, transparent);
  --gb-rule: color-mix(in srgb, var(--gb-on-accent-base) 35%, transparent);
}

/* The band floods edge to edge; its contents line up with the page above it. */
.bandInner {
  max-width: var(--gb-measure);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-8);
}

/* --- Layout helpers ----------------------------------------------------- */

.g2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
  gap: var(--gb-space-8);
}

.g3 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: var(--gb-space-6);
}

.g4 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: var(--gb-space-4);
}

.ruleHeavy {
  border: 0;
  border-top: 2px solid var(--gb-text);
  margin: 0;
}

/* --- Channel bar -------------------------------------------------------- */

/*
 * The panel bar. A terminator, used once per page.
 *
 * Four segments alternating between the panel's accent and its type colour.
 * This was the four channels side by side, which put every signal colour the
 * brand owns onto every page — on the store alone that was four hues in a
 * palette allowed three. Alternating two of the panel's own colours keeps the
 * device (a hard, segmented full-width rule that says "this section is over")
 * while carrying the panel's identity rather than the whole system's.
 */
.channelBar {
  display: flex;
  gap: 2px;
  height: 0.5rem;
}
.channelBar i {
  flex: 1;
  display: block;
}
.channelBar i:nth-child(odd) {
  background: var(--gb-accent);
}
.channelBar i:nth-child(even) {
  background: var(--gb-text);
}

/* --- Buttons ------------------------------------------------------------ */

.cta {
  display: inline-block;
  font-family: var(--gb-font-text);
  font-variation-settings:
    'wdth' 112,
    'wght' 800;
  text-transform: uppercase;
  font-size: var(--gb-ui-label);
  letter-spacing: 0.04em;
  padding: var(--gb-space-3) var(--gb-space-6);
  text-decoration: none;
  border: 1px solid transparent;
}

/*
 * The one action a page exists to ask for, at the size of that.
 *
 * Not a third variant — it composes with either of the two below, and it changes
 * nothing but the scale: same face, same tracking, same fill, same hover. It was
 * added for a page that spent a full screen arguing somebody should act, and
 * then asked them to with a button the same size as the secondary link beside
 * it.
 *
 * The type runs to 24px on a monitor, which is the guidelines' subhead step
 * rather than a display one: this is a button, and a button set at headline size
 * stops reading as something you press. The padding is in `em` so the box grows
 * with the label rather than being re-tuned at every width.
 */
.ctaLead {
  font-size: clamp(1rem, 0.6rem + 1.1vw, 1.5rem);
  padding: 0.7em 1.4em;
}

/*
 * The filled button is the panel's accent, wherever it is. There is no second
 * filled variant: a page with two differently coloured primary buttons has two
 * primary actions, which means it has none.
 */
.ctaPrimary {
  background: var(--gb-accent);
  color: var(--gb-on-accent);
}

/*
 * The same button, with nowhere to go yet.
 *
 * It is the sunken ground rather than the accent, the rule rather than a
 * transparent border, and the faint step of the type ramp — the three roles this
 * system already uses for "present, and not the thing you act on". Nothing
 * fills, nothing lifts, and the press animation in `tokens.css` is already
 * written as `button:not(:disabled):active`, so it excludes this by construction
 * rather than by a rule here remembering to.
 *
 * The resets are what a `<button>` needs to sit in a row of anchors without
 * being a pixel taller or a hair differently spaced: `line-height` because a
 * button's is `normal` where an anchor inherits the page's, and `appearance`
 * because Safari otherwise insists on drawing its own control over the top.
 *
 * `--gb-text-faint` is a deliberate exception to the 70% ramp rule for labels
 * that carry meaning. This label carries none while it is in this state — there
 * is nothing behind it — and the whole job of the colour here is to say so.
 */
.ctaPending {
  background: var(--gb-surface-sunken);
  border-color: var(--gb-rule);
  color: var(--gb-text-faint);
  cursor: not-allowed;
  line-height: inherit;
  appearance: none;
  margin: 0;
}

/*
 * The outline button, which fills with its own border colour on hover.
 *
 * The fill wipes in from the left rather than fading, because that is the
 * gesture the rest of the system already uses — the channel bar wipes, the
 * navigation underlines wipe, the progress strip grows. A fade would be a
 * second idea about how things arrive here.
 *
 * The fill is a pseudo-element rather than an animated `background-color`
 * because a colour cannot be animated directionally, and `isolation: isolate`
 * keeps the `z-index: -1` inside the button rather than dropping it behind the
 * page. The border is left in place and the fill covers the padding box, so
 * the button's footprint does not shift by a pixel as it fills.
 */
.ctaGhost {
  border-color: var(--gb-text);
  color: var(--gb-text);
  position: relative;
  isolation: isolate;
  overflow: hidden;
}

.ctaGhost::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  background: var(--gb-text);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform var(--gb-motion) var(--gb-ease-out);
}

/*
 * The text inverts to the ground it is sitting on. `--gb-surface` is redefined
 * by the flood bands and by each audience space, so this is correct on Paper,
 * on Ink and on a blue band without a rule per case.
 */
.ctaGhost:hover,
.ctaGhost:focus-visible {
  color: var(--gb-surface);
}

.ctaGhost:hover::before,
.ctaGhost:focus-visible::before {
  transform: scaleX(1);
}

/*
 * The bands need no button rule of their own any more.
 *
 * On the Build band the flood *is* the accent, so `.bandBuild` inverts
 * `--gb-accent` to white and the primary button follows it. On the Ink band the
 * panel's accent still reads against black, so it is simply left alone. The Ink
 * band used to force the button to Volt, which meant every dark band on the
 * site ended in a lime rectangle claiming something was live.
 */

/* --- Motion ------------------------------------------------------------- */

/*
 * The progress strip draws itself.
 *
 * Each bar grows from the baseline in sequence, left to right, which is the
 * direction the device already means: this got better, and we measured it. It
 * is the one animation in the system that is *about* the content rather than
 * about the interface, so it is the one worth spending a third of a second on.
 *
 * `transform-origin: bottom` and a scale rather than an animated height,
 * because height is laid out and transform is not — a strip of twelve bars
 * animating height would reflow twelve times a frame.
 *
 * Not applied to `.stripTiny`, which is the mark in the navigation bar. The
 * wordmark should be solid the instant the page paints; a logo that assembles
 * itself on every navigation is a novelty that wears out on the second page.
 */
@keyframes gb-bar-grow {
  from {
    transform: scaleY(0);
  }
  to {
    transform: scaleY(1);
  }
}

.stripSm i,
.stripMd i,
.stripLg i {
  transform-origin: bottom;
  animation: gb-bar-grow var(--gb-motion-slow) var(--gb-ease-out) both;
}

/*
 * The stagger, written out.
 *
 * `nth-child` rather than an inline custom property per bar, so the component
 * stays a plain map over versions and no delay arithmetic ships in the markup.
 * Twelve is where it stops: past that the whole strip arrives together, because
 * nobody is watching bar nineteen wait its turn.
 */
.stripSm i:nth-child(1),
.stripMd i:nth-child(1),
.stripLg i:nth-child(1) {
  animation-delay: 0ms;
}
.stripSm i:nth-child(2),
.stripMd i:nth-child(2),
.stripLg i:nth-child(2) {
  animation-delay: 40ms;
}
.stripSm i:nth-child(3),
.stripMd i:nth-child(3),
.stripLg i:nth-child(3) {
  animation-delay: 80ms;
}
.stripSm i:nth-child(4),
.stripMd i:nth-child(4),
.stripLg i:nth-child(4) {
  animation-delay: 120ms;
}
.stripSm i:nth-child(5),
.stripMd i:nth-child(5),
.stripLg i:nth-child(5) {
  animation-delay: 160ms;
}
.stripSm i:nth-child(6),
.stripMd i:nth-child(6),
.stripLg i:nth-child(6) {
  animation-delay: 200ms;
}
.stripSm i:nth-child(7),
.stripMd i:nth-child(7),
.stripLg i:nth-child(7) {
  animation-delay: 240ms;
}
.stripSm i:nth-child(8),
.stripMd i:nth-child(8),
.stripLg i:nth-child(8) {
  animation-delay: 280ms;
}
.stripSm i:nth-child(n + 9),
.stripMd i:nth-child(n + 9),
.stripLg i:nth-child(n + 9) {
  animation-delay: 320ms;
}

/*
 * The channel bar wipes in from the left.
 *
 * It is a terminator — it means "this section is over" — so it arrives as one
 * gesture rather than four, and only once per page.
 */
.channelBar {
  animation: gb-wipe var(--gb-motion-slow) var(--gb-ease-out) both;
  transform-origin: left;
}

@keyframes gb-wipe {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

/* The calls to action lift, the same two pixels as everything else. */
.cta {
  transition:
    transform var(--gb-motion) var(--gb-ease-out),
    background-color var(--gb-motion) var(--gb-ease),
    color var(--gb-motion) var(--gb-ease);
}

.cta:hover {
  transform: translateY(-2px);
}

/*
 * A stat's number counts for nothing if it is wrong, so nothing here animates
 * the value — only the rule above it, which draws in with the block. A number
 * that ticks up through values it never had is the one animation this product
 * genuinely cannot have.
 */
.stat {
  transition: color var(--gb-motion) var(--gb-ease);
}

@media (prefers-reduced-motion: reduce) {
  .stripSm i,
  .stripMd i,
  .stripLg i,
  .channelBar {
    animation: none;
    transform: none;
  }

  .cta:hover {
    transform: none;
  }
}

/* --- The loader --------------------------------------------------------- */

/*
 * Waiting, drawn as the mark.
 *
 * Every surface in this product used to wait by printing the word `Loading…` in
 * the empty state's dashed box — the same component a table with no rows gets,
 * so *we are still asking* and *there is nothing here* looked identical for as
 * long as the request took. On a games platform, of all things.
 *
 * So the mark runs. Five ascending bars, a light travelling up the staircase
 * left to right, and then a beat before it goes again. The cycle is three of
 * the slow duration and the light passes in the first half of it, which is what
 * stops a fast connection reading as a strobe — an animation with no rest in it
 * is a strobe, and this one sits in the corner of a page for as long as a
 * request takes.
 *
 * **The heights never move.** Section 03's one rule is that a strip must never
 * draw a shape its data did not produce, and a loader that animated bar heights
 * would be a rising staircase every game got for free while it waited. What
 * moves is the colour and a three-pixel lift, so the silhouette on screen is
 * exactly the logo throughout.
 *
 * **The colour is the surface's**, which is the whole reason this is worth
 * having. `--gb-accent` is Build in a studio's portal, Hot on a creator's
 * board, Solar on the store, so the same component waits in the colours of
 * whoever is reading — and nothing has to pass it a channel to get that right.
 */
.loader {
  display: inline-flex;
  align-items: center;
  gap: var(--gb-space-3);
}

/* The page-level wait, rather than the one beside a button. */
.loaderBlock {
  display: flex;
  justify-content: center;
  padding: var(--gb-space-12) 0;
}

/*
 * The whole window is the wait.
 *
 * `.loaderBlock` waits inside a page that has already been drawn — a table
 * still loading under a heading that is already true. This is for the moments
 * before there is a page at all: the session check that runs on every load, and
 * the frame between deciding to send somebody somewhere else and the browser
 * going. Both of those rendered an empty fragment, which is a white or black
 * rectangle with nothing on it for the length of a network request.
 *
 * The ground comes from `[data-gb-space]` in `tokens.css`, which paints
 * `--gb-surface` on anything carrying the attribute — so this is the audience's
 * own colour rather than the default one, and the wait already says which side
 * of the product is arriving.
 */
.loaderPage {
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--gb-space-8) var(--gb-gutter);
}

/*
 * The same wait, over a page that is still on screen.
 *
 * For a navigation rather than a first load: the page being left is perfectly
 * good and stays where it is until the next one is ready, which is a better
 * answer than replacing it with a loader and then replacing that.
 *
 * Above the sticky bar (`z-index: 20`) and the select's open list (`30`),
 * because a navigation is not a state anything should be usable during — a
 * second click on the bar while the first is in flight is how somebody ends up
 * somewhere they did not choose.
 */
.loaderOver {
  position: fixed;
  inset: 0;
  z-index: 40;
  min-height: 0;
}

.loaderStrip {
  display: inline-flex;
  align-items: flex-end;
  gap: 3px;
  line-height: 0;
}

/*
 * Its own size classes rather than `.stripSm` and friends, deliberately.
 * Those carry `gb-bar-grow` — the one-shot assemble the strip does when a page
 * paints — and a bar cannot run two animations on the same property. Sharing
 * them would have meant unpicking that with `nth-child` selectors of matching
 * specificity, which is a thing to get wrong every time either animation is
 * touched.
 */
.loaderStrip i {
  display: block;
  background-color: var(--gb-rule);
  animation: gb-loader-run calc(var(--gb-motion-slow) * 3) var(--gb-ease) infinite;
}

.loaderSm {
  height: 1.25rem;
  gap: 2px;
}
.loaderSm i {
  width: 0.55rem;
}
.loaderMd {
  height: 2.25rem;
}
.loaderMd i {
  width: 1rem;
}
.loaderLg {
  height: 5rem;
  gap: 4px;
}
.loaderLg i {
  width: 2rem;
}

/*
 * The stagger, written out — same reason as the strip's above, and literal for
 * the same reason too. 120ms apart over five bars is 480ms of travel inside a
 * 1020ms cycle, so the mark rests for the remainder rather than restarting the
 * instant it finishes.
 */
.loaderStrip i:nth-child(1) {
  animation-delay: 0ms;
}
.loaderStrip i:nth-child(2) {
  animation-delay: 120ms;
}
.loaderStrip i:nth-child(3) {
  animation-delay: 240ms;
}
.loaderStrip i:nth-child(4) {
  animation-delay: 360ms;
}
.loaderStrip i:nth-child(5) {
  animation-delay: 480ms;
}

.loaderIdent i {
  transform-origin: 50% 100%;
  animation:
    gb-ident-raise var(--gb-motion-slow) var(--gb-ease-emphasis) backwards,
    gb-loader-run calc(var(--gb-motion-slow) * 3) var(--gb-ease) infinite;
}

@keyframes gb-ident-raise {
  from {
    transform: scaleY(0);
  }
  to {
    transform: scaleY(1);
  }
}

/*
 * Dim, then the accent as the light arrives, then type colour behind it, then
 * dim again. Flat fills throughout: the accent does not fade through a tint of
 * itself, it is on and then it is not, which is the same rule the rest of the
 * system paints by.
 */
@keyframes gb-loader-run {
  0% {
    background-color: var(--gb-rule);
    transform: translateY(0);
  }
  14% {
    background-color: var(--gb-accent);
    transform: translateY(-3px);
  }
  32% {
    background-color: var(--gb-text);
    transform: translateY(0);
  }
  70%,
  100% {
    background-color: var(--gb-rule);
    transform: translateY(0);
  }
}

/*
 * Reduced motion gets the mark standing still, not the mark frozen mid-cycle.
 *
 * The blanket rule in `tokens.css` sets every animation to one iteration of
 * 0.01ms, which for an infinite loader means it plays once and stops wherever
 * that leaves it. Naming `animation: none` here is what makes the resting state
 * a decision: the logo exactly as it is drawn everywhere else, accent on top.
 */
@media (prefers-reduced-motion: reduce) {
  .loaderStrip i,
  .loaderIdent i {
    animation: none;
    transform: none;
    background-color: var(--gb-rule);
  }

  .loaderStrip i:last-child {
    background-color: var(--gb-accent);
  }
}

.loaderLabel {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-muted);
}
